import { inject, Injectable } from '@angular/core';
import { Actions, ofType, createEffect } from '@ngrx/effects';
import { catchError, map, mergeMap, of, tap, withLatestFrom } from 'rxjs';
import { Store } from '@ngrx/store';
import * as ReviewEntriesActions from './../actions/review-entries.actions';
import { ReviewEntriesService } from '../../review-entries.service';
import {
  areGroupsLoaded,
  selectGroupsSupervisorNumber,
  areDepartmentsLoaded,
  arePendingRecordsLoaded,
  areCountsLoaded,
  selectGroups,
  selectDepartments,
  selectCounts,
  selectPendingRecords,
  isCalendarInfoLoaded,
  selectCalendarInfo,
  selectCalendarInfoKey,
  areSupervisorHeadDetailsLoaded,
  selectSupervisorHeadDetails,
  isSupervisorHeadDetailsLoaded
} from './../selectors/review-entries.selectors';
import { HomeService } from '@app/pages/home/home.service';
import { CalendarService } from '@app/core/services/calendar.service';

@Injectable()
export class ReviewEntriesEffects {
  private readonly actions$ = inject(Actions);
  private readonly reviewService = inject(ReviewEntriesService);
  private readonly store = inject(Store);
  private readonly homeService = inject(HomeService);
  private readonly calendarService = inject(CalendarService);

  // Load Groups Effect with Memoization
  loadGroups$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ReviewEntriesActions.loadGroups),
      withLatestFrom(
        this.store.select(areGroupsLoaded),
        this.store.select(selectGroups),
        this.store.select(selectGroupsSupervisorNumber)
      ),
      mergeMap(([action, groupsLoaded, cachedGroups, loadedForSupervisor]) => {
        const supervisorMismatch =
          loadedForSupervisor != null &&
          action.supervisorNumber != null &&
          loadedForSupervisor !== action.supervisorNumber;
        const shouldFetch =
          action.forceRefresh ||
          !groupsLoaded ||
          cachedGroups.length === 0 ||
          supervisorMismatch;

        if (shouldFetch) {
          return this.reviewService.getGroups(action.supervisorNumber).pipe(
            map((groups) =>
              ReviewEntriesActions.loadGroupsSuccess({
                groups,
                supervisorNumber: action.supervisorNumber
              })
            ),
            catchError((error) =>
              of(ReviewEntriesActions.loadGroupsFailure({ error }))
            )
          );
        } else {
          return of(
            ReviewEntriesActions.loadGroupsSuccess({
              groups: cachedGroups,
              supervisorNumber: action.supervisorNumber
            })
          );
        }
      })
    )
  );

  // Load Departments Effect with Memoization
  loadDepartments$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ReviewEntriesActions.loadDepartments),
      withLatestFrom(
        this.store.select(areDepartmentsLoaded),
        this.store.select(selectDepartments),
      ), // Check if departments are already loaded
      mergeMap(([action, departmentsLoaded, cachedDepartments]) => {
        if (
          action.forceRefresh ||
          !departmentsLoaded ||
          cachedDepartments.length === 0
        ) {
          return this.reviewService.getSupervisorDepartmentDetails(action.supervisorNumber,
            action.directReportsOnly).pipe(
              map((departments) =>
                ReviewEntriesActions.loadDepartmentsSuccess({ departments })
              ),
              catchError((error) =>
                of(ReviewEntriesActions.loadDepartmentsFailure({ error }))
              )
            );
        } else {
          return of(
            ReviewEntriesActions.loadDepartmentsSuccess({ departments: cachedDepartments })
          );
        }
      })
    )
  );

  // Load Pending Records Effect with Memoization
  loadPendingRecords$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ReviewEntriesActions.loadPendingRecords),
      withLatestFrom(
        this.store.select(arePendingRecordsLoaded),
        this.store.select(selectPendingRecords)
      ), // Check if pending records are already loaded
      mergeMap(([action, departmentsLoaded, cachedPendingRecords]) => {
        // Add a force refresh option
        if (!departmentsLoaded || cachedPendingRecords.length === 0) {
          return this.reviewService.getPageSupervisorDetailsPending(
            action.supervisorNumber,
            action.selectedDate,
            action.employeeNumber,
            action.page,
            action.itemsPerPage,
            '',
            '',
            action.pageSearchColumn,
            action.pageSearchValue,
            action.directReportsOnly,
            action.noOfDays,
            action.supervisorReportsOnly
          ).pipe(
            map((pendingRecordsResponse) =>
              ReviewEntriesActions.loadPendingRecordsSuccess({
                pendingRecords: pendingRecordsResponse.records,
                totalHours: pendingRecordsResponse.totalHours,
                totalEquipmentHours:
                  pendingRecordsResponse.totalEquipmentHours,
              })
            ),
            catchError((error) =>
              of(ReviewEntriesActions.loadPendingRecordsFailure({ error }))
            )
          );
        } else {
          return of(
            ReviewEntriesActions.loadPendingRecordsSuccess({
              pendingRecords: cachedPendingRecords,
              totalHours: 0,
              totalEquipmentHours: 0,
            })
          );
        }
      })
      /* mergeMap(([action, pendingRecordsLoaded]) => {
        if (pendingRecordsLoaded) {
          // If pending records are already loaded, skip API call
          return of(
            ReviewEntriesActions.loadPendingRecordsSuccess({
              pendingRecords: [],
              totalHours: 0,
              totalEquipmentHours: 0,
            }) // Optionally use cached data
          );
        } else {
          return this.reviewService
            .getPageSupervisorDetailsPending(
              action.supervisorNumber,
              action.selectedDate,
              action.employeeNumber,
              action.page,
              action.itemsPerPage,
              '',
              '',
              action.pageSearchColumn,
              action.pageSearchValue,
              action.directReportsOnly,
              action.noOfDays
            )
            .pipe(
              map((pendingRecordsResponse: any) =>
                ReviewEntriesActions.loadPendingRecordsSuccess({
                  pendingRecords: pendingRecordsResponse.records,
                  totalHours: pendingRecordsResponse.totalHours,
                  totalEquipmentHours:
                    pendingRecordsResponse.totalEquipmentHours,
                })
              ),
              catchError((error) =>
                of(ReviewEntriesActions.loadPendingRecordsFailure({ error }))
              )
            );
        }
      }) */
    )
  );

  // Load Calendar Counts Effect with Memoization
  loadCalendarCounts$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ReviewEntriesActions.loadCalendarCounts),
      withLatestFrom(
        this.store.select(areCountsLoaded),
        this.store.select(selectCounts)
      ), // Check if calendar counts are already loaded
      mergeMap(([action, departmentsLoaded, cachedCounts]) => {
        if (
          action.forceRefresh ||
          !departmentsLoaded ||
          cachedCounts.length === 0
        ) {
          return this.reviewService.getCalendarCounts(
            action.supervisorNumber,
            action.showAll,
            action.groupID,
            action.department,
            action.currentMonthDate
          ).pipe(
            map((counts) =>
              ReviewEntriesActions.loadCalendarCountsSuccess({ counts })
            ),
            catchError((error) =>
              of(ReviewEntriesActions.loadCalendarCountsFailure({ error }))
            )
          );
        } else {
          return of(
            ReviewEntriesActions.loadCalendarCountsSuccess({ counts: cachedCounts })
          );
        }
      })
      /* mergeMap(([action, countsLoaded]) => {
        if (countsLoaded) {
          // If counts are already loaded, skip API call
          return of(
            ReviewEntriesActions.loadCalendarCountsSuccess({ counts: [] }) // Optionally use cached data
          );
        } else {
          return this.reviewService
            .getCalendarCounts(
              action.supervisorNumber,
              action.showAll,
              action.groupID,
              action.department
            )
            .pipe(
              map((counts) =>
                ReviewEntriesActions.loadCalendarCountsSuccess({ counts })
              ),
              catchError((error) =>
                of(ReviewEntriesActions.loadCalendarCountsFailure({ error }))
              )
            );
        }
      }) */
    )
  );

  // Load Calendar Info Effect with Memoization
  loadCalendarInfo$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ReviewEntriesActions.loadCalendarInfo),
      withLatestFrom(
        this.store.select(isCalendarInfoLoaded),
        this.store.select(selectCalendarInfo),
        this.store.select(selectCalendarInfoKey)
      ),
      mergeMap(([action, isLoaded, cachedInfo, cachedKey]) => {
        const requestKey = `${action.employeeNumber}|${action.calendarDate}`;
        const shouldReload =
          action.forceRefresh ||
          !isLoaded ||
          cachedKey !== requestKey;

        const publishCalendar = (response: any) => {
          const events = Array.isArray(response) ? [...response] : [];
          this.calendarService.setData(events);
          return events;
        };

        const successAction = (response: any) =>
          ReviewEntriesActions.loadCalendarInfoSuccess({
            calendarInfo: response,
            employeeNumber: action.employeeNumber,
            calendarDate: action.calendarDate
          });

        if (shouldReload) {
          return this.homeService
            .getCalendarEvents(
              action.employeeNumber,
              action.calendarDate,
              !!action.forceRefresh
            )
            .pipe(
              tap((response) => publishCalendar(response)),
              map((response) => successAction(response)),
              catchError((error) =>
                of(ReviewEntriesActions.loadCalendarInfoFailure({ error }))
              )
            );
        }

        return of(cachedInfo).pipe(
          tap((response) => {
            if (response) {
              publishCalendar(response);
            }
          }),
          map((response) => successAction(response))
        );
      })
    )
  );

  // Load Groups Effect with Memoization
  loadSupervisorHeadEmpDetails$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ReviewEntriesActions.loadSupervisorHeadEmpDetails),
      withLatestFrom(
        this.store.select(areSupervisorHeadDetailsLoaded),
        this.store.select(selectSupervisorHeadDetails)
      ),
      mergeMap(([action, isLoaded, cachedData]) => {
        if (!isLoaded) {
          return this.reviewService
            .getPageSupervisorHeadEmpDetails(
              action.supervisorNumber,
              action.selectedDate,
              null,
              action.pageSearchColumn,
              action.pageSearchValue,
              action.directReportsOnly,
              action.noOfDays,
              action.supervisorReportsOnly
            )
            .pipe(
              map((data) =>
                ReviewEntriesActions.loadSupervisorHeadEmpDetailsSuccess({
                  supervisorHeadEmpDetails: data,
                })
              ),
              catchError((error) =>
                of(ReviewEntriesActions.loadSupervisorHeadEmpDetailsFailure({ error }))
              )
            );
        } else {
          // Return cached data
          return of(
            ReviewEntriesActions.loadSupervisorHeadEmpDetailsSuccess({
              supervisorHeadEmpDetails: cachedData,
            })
          );
        }
      })
    )
  );

  // load Page Supervisor Head Details with Memoization
  loadPageSupervisorHeadDetails$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ReviewEntriesActions.loadPageSupervisorHeadDetails),
      // withLatestFrom(
      //   this.store.select(isSupervisorHeadDetailsLoaded, (props: any) => props) // Pass props correctly
      // ),
      withLatestFrom(
        this.store.select(isSupervisorHeadDetailsLoaded, (props: any) => props),
        this.store.select(selectSupervisorHeadDetails)
      ),
      mergeMap(([action, isLoaded]) => {
        if (!isLoaded) {
          return this.reviewService
            .getPageSupervisorHeadDetails(
              action.supervisorNumber,
              action.selectedDate,
              action.employeeNumber,
              action.pageSearchColumn,
              action.pageSearchValue,
              action.directReportsOnly,
              action.noOfDays,
              action.supervisorReportsOnly
            )
            .pipe(
              map((data) =>
                ReviewEntriesActions.loadPageSupervisorHeadDetailsSuccess({ headDetails: data })
              ),
              catchError((error) =>
                of(ReviewEntriesActions.loadPageSupervisorHeadDetailsFailure({ error }))
              )
            );
        } else {
          // Skip API call and return empty observable
          return of();
        }
      })
    )
  );


}
